import React, { useEffect, useMemo, useState } from "react";
import { Megaphone, Plus, Pencil, Trash2, ExternalLink, Eye, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const EMPTY = { text: "", url: "", is_active: true };

export default function InstituteScroller() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/scrollers");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      text: it.text || "",
      url: it.url || "",
      is_active: !!it.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    const text = form.text.trim();
    const url = form.url.trim();
    if (!text) {
      toast.error("Scroller text is required");
      return;
    }
    if (text.length > 300) {
      toast.error("Scroller text must be 300 characters or fewer");
      return;
    }
    if (url && !/^https?:\/\//i.test(url)) {
      toast.error("Redirect link must start with http:// or https://");
      return;
    }
    setSaving(true);
    try {
      const payload = { text, url, is_active: !!form.is_active };
      if (editing) {
        const { data } = await api.put(
          `/institute/scrollers/${editing.id}`,
          payload
        );
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Scroller updated");
      } else {
        const { data } = await api.post("/institute/scrollers", payload);
        setItems((arr) => [...arr, data]);
        toast.success("Scroller added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (it) => {
    const prev = items;
    setItems((arr) =>
      arr.map((x) => (x.id === it.id ? { ...x, is_active: !x.is_active } : x))
    );
    try {
      const { data } = await api.put(`/institute/scrollers/${it.id}`, {
        text: it.text,
        url: it.url,
        is_active: !it.is_active,
      });
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
    } catch (err) {
      setItems(prev);
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not toggle");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institute/scrollers/${confirmDelete.id}`);
      setItems((arr) => arr.filter((x) => x.id !== confirmDelete.id));
      toast.success("Scroller deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  const activePreview = useMemo(
    () => items.filter((x) => x.is_active && x.text.trim()),
    [items]
  );

  return (
    <div className="p-8 md:p-12 max-w-5xl mx-auto" data-testid="inst-scroller-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
            Website Updates
          </div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17] flex items-center gap-3">
            <Megaphone size={28} strokeWidth={1.5} className="text-amber-600" />
            Announcement Scrollers
          </h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Add multiple scrolling announcements that appear between the main
            navigation and the page content on your public website. All active
            entries scroll together — use the active toggle to publish or
            hide individual messages.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            data-testid="inst-scroller-add"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-4 py-2 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Scroller
          </button>
        )}
      </div>

      {/* Live preview of what visitors see */}
      {activePreview.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2 flex items-center gap-1.5">
            <Eye size={11} strokeWidth={1.5} /> Live Preview
          </div>
          <div
            className="bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-sm overflow-hidden"
            data-testid="inst-scroller-preview"
          >
            <style>{`
              @keyframes inst-scroller-pv {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
            `}</style>
            <div className="px-3 py-1.5 flex items-center gap-3">
              <span className="hidden sm:inline-flex text-[10px] tracking-[0.25em] uppercase font-bold bg-white/20 px-2 py-0.5 rounded-sm shrink-0">
                Announcement
              </span>
              <div className="flex-1 overflow-hidden">
                <span
                  className="inline-block whitespace-nowrap text-sm font-medium"
                  style={{ animation: "inst-scroller-pv 30s linear infinite" }}
                >
                  {[...activePreview, ...activePreview].map((s, i) => (
                    <span key={`${s.id}-${i}`} className="px-3">
                      <span className="opacity-80 mr-2">▸</span>
                      {s.text}
                    </span>
                  ))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6b655b]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#6b655b]">
            No scrollers yet.{" "}
            {canEdit && (
              <span>Click "Add Scroller" to publish your first message.</span>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-[#eee9de]">
            {items.map((it) => (
              <li
                key={it.id}
                className="p-4 sm:p-5 hover:bg-[#fbf9f4] flex items-start gap-4 flex-wrap"
                data-testid={`inst-scroller-row-${it.id}`}
              >
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full ring-1 font-semibold ${
                        it.is_active
                          ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                          : "bg-slate-100 text-slate-700 ring-slate-200"
                      }`}
                    >
                      {it.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[#1c1a17] leading-relaxed whitespace-pre-wrap">
                    {it.text}
                  </p>
                  {it.url && (
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                    >
                      {it.url} <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2 shrink-0">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!it.is_active}
                        onChange={() => toggleActive(it)}
                        className="w-4 h-4 accent-[#234333]"
                        data-testid={`inst-scroller-toggle-${it.id}`}
                      />
                      <span className="text-xs text-[#6b655b]">Active</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => openEdit(it)}
                      title="Edit"
                      data-testid={`inst-scroller-edit-${it.id}`}
                      className="p-2 text-[#234333] hover:bg-[#f0ece2] rounded-sm"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(it)}
                      title="Delete"
                      data-testid={`inst-scroller-delete-${it.id}`}
                      className="p-2 text-rose-600 hover:bg-rose-50 rounded-sm"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Scroller" : "Add Scroller"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Display Text *
              </div>
              <input
                data-testid="inst-scroller-form-text"
                className={inputCls}
                maxLength={300}
                placeholder="e.g., Admissions 2025-26 are now open — Apply by 30 June"
                value={form.text}
                onChange={(e) => set("text", e.target.value)}
                autoFocus
              />
              <div className="mt-1 text-[11px] text-[#6b655b]">
                {form.text.length}/300 characters
              </div>
            </label>
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Redirect Link / URL (optional)
              </div>
              <input
                data-testid="inst-scroller-form-url"
                type="url"
                className={inputCls}
                maxLength={500}
                placeholder="https://example.com/admissions"
                value={form.url}
                onChange={(e) => set("url", e.target.value)}
              />
              <div className="mt-1 text-[11px] text-[#6b655b]">
                Leave empty for a non-clickable announcement.
              </div>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => set("is_active", e.target.checked)}
                className="w-4 h-4 accent-[#234333]"
                data-testid="inst-scroller-form-active"
              />
              <span className="text-sm text-[#1c1a17]">
                Active — show on public website
              </span>
            </label>
          </div>
          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm rounded-sm text-[#1c1a17] hover:bg-[#f0ece2]"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="inst-scroller-form-save"
              className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-4 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Saving…
                </>
              ) : (
                <>Save</>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this scroller?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.text}" will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="inst-scroller-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
